import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./Header.css";
import { CalendarDays } from "lucide-react";
import FiltersPanel from "./FiltersPanel";
import HamburgerMenu from "./HamburgerMenu";
import DiveStories from "./DiveStories";

const Header = ({
  currentUser,
  onLogout,
  filterDate,
  onFilterChange,
  onCantonChange,
  onTypeChange,
  onGasChange,
  onConfigChange,
  onFiltersPanelChange,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const headerRef = useRef(null);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const setVar = () => {
      document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`);
    };

    setVar();

    const ro = new ResizeObserver(setVar);
    ro.observe(el);

    window.addEventListener("resize", setVar);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setVar);
    };
  }, []);

  // États locaux
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);
  const [selectedCanton, setSelectedCanton] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedGas, setSelectedGas] = useState("");
  const [selectedConfig, setSelectedConfig] = useState("");

  // --- Panneau Filtres Mobile ---
  const [isPanelOpen, setPanelOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Notifier le parent quand le panneau filtres s'ouvre/ferme
  useEffect(() => {
    onFiltersPanelChange?.(isPanelOpen);
  }, [isPanelOpen, onFiltersPanelChange]);

  // Fermer le calendrier quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Réinitialiser tous les filtres
  const handleResetFilters = () => {
    setSelectedCanton("");
    setSelectedType("");
    setSelectedGas("");
    setSelectedConfig("");
    onFilterChange("");
    if (onCantonChange) onCantonChange({ target: { value: "" } });
    if (onTypeChange) onTypeChange({ target: { value: "" } });
    if (onGasChange) onGasChange({ target: { value: "" } });
    if (onConfigChange) onConfigChange({ target: { value: "" } });
  };

  const hasActiveFilters =
    !!filterDate ||
    !!selectedCanton ||
    !!selectedType ||
    !!selectedGas ||
    !!selectedConfig;

  return (
    <header className="header" ref={headerRef}>
      {/* Ligne du haut */}
      <div className="header-top">
        <div className="header-left"></div>

        {/* Menu hamburger */}
        <div className="header-right">
          {!isMobile && (
            <button
              type="button"
              className="header-plan-btn"
              onClick={() => navigate("/?create=1")}
            >
              + {t('create.button')}
            </button>
          )}
          <HamburgerMenu
            onLogout={onLogout}
            onProfileClick={() => navigate("/profile")}
            onAboutClick={() => navigate("/about")}
            onMapClick={() => navigate("/")}
            onCommunityClick={() => navigate("/community")}
          />
        </div>
      </div>

      {/* Bandeau "stories" des plongées à venir (remplace l'ancien "Bienvenue") */}
      <DiveStories currentUser={currentUser} />

      {/* Ligne du bas : filtres */}
      <div className="header-bottom">
        {isMobile ? (
          <>

          </>
        ) : (
          <div className="header-bottom-right">
            {/* Desktop : filtres normaux */}
            <div
              className={`filter-column ${showCalendar ? "show-calendar" : ""}`}
              ref={calendarRef}
            >
              <label>{t('filters.date')}</label>
              <span
                className="calendar-icon"
                title={t('filters.chooseDate')}
                onClick={() => setShowCalendar(!showCalendar)}
              >
                <CalendarDays
                  size={22}
                  strokeWidth={2.2}
                  color="#222"
                  style={{
                    marginRight: 4,
                    cursor: "pointer",
                    verticalAlign: "middle",
                  }}
                />
              </span>

              {showCalendar && (
                <div className="calendar-popup">
                  <Calendar
                    onChange={(date) => {
                      const d = new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate()
                      );
                      const yyyy = d.getFullYear();
                      const mm = String(d.getMonth() + 1).padStart(2, "0");
                      const dd = String(d.getDate()).padStart(2, "0");
                      const iso = `${yyyy}-${mm}-${dd}`;
                      onFilterChange(iso);
                      setShowCalendar(false);
                    }}
                    value={
                      filterDate
                        ? new Date(`${filterDate}T00:00:00`)
                        : new Date()
                    }
                    locale={i18n.language}
                  />
                </div>
              )}
            </div>

            <div className="filter-column">
              <label>{t('filters.canton')}</label>
              <select
                value={selectedCanton}
                onChange={(e) => {
                  setSelectedCanton(e.target.value);
                  if (onCantonChange) onCantonChange(e);
                }}
              >
                <option value="">{t('filters.all')}</option>
                <option value="AG">{t('cantons.AG')}</option>
                <option value="AI">{t('cantons.AI')}</option>
                <option value="AR">{t('cantons.AR')}</option>
                <option value="BE">{t('cantons.BE')}</option>
                <option value="BL">{t('cantons.BL')}</option>
                <option value="BS">{t('cantons.BS')}</option>
                <option value="FR">{t('cantons.FR')}</option>
                <option value="GE">{t('cantons.GE')}</option>
                <option value="GL">{t('cantons.GL')}</option>
                <option value="GR">{t('cantons.GR')}</option>
                <option value="JU">{t('cantons.JU')}</option>
                <option value="LU">{t('cantons.LU')}</option>
                <option value="NE">{t('cantons.NE')}</option>
                <option value="NW">{t('cantons.NW')}</option>
                <option value="OW">{t('cantons.OW')}</option>
                <option value="SG">{t('cantons.SG')}</option>
                <option value="SH">{t('cantons.SH')}</option>
                <option value="SO">{t('cantons.SO')}</option>
                <option value="SZ">{t('cantons.SZ')}</option>
                <option value="TG">{t('cantons.TG')}</option>
                <option value="TI">{t('cantons.TI')}</option>
                <option value="UR">{t('cantons.UR')}</option>
                <option value="VD">{t('cantons.VD')}</option>
                <option value="VS">{t('cantons.VS')}</option>
                <option value="ZG">{t('cantons.ZG')}</option>
                <option value="ZH">{t('cantons.ZH')}</option>
              </select>
            </div>

            <div className="filter-column">
              <label>{t('filters.type')}</label>
              <select
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  if (onTypeChange) onTypeChange(e);
                }}
              >
                <option value="">{t('filters.all')}</option>
                <option value="egal">{t('filters.anyChoice')}</option>
                <option value="loisir">{t('diveTypes.loisir')}</option>
                <option value="deco">{t('diveTypes.deco')}</option>
              </select>
            </div>

            <div className="filter-column">
              <label>{t('filters.gas')}</label>
              <select
                value={selectedGas}
                onChange={(e) => {
                  setSelectedGas(e.target.value);
                  if (onGasChange) onGasChange(e);
                }}
              >
                <option value="">{t('filters.all')}</option>
                <option value="egal">{t('filters.anyChoice')}</option>
                <option value="air">{t('gasTypes.air')}</option>
                <option value="nx">{t('gasTypes.nx')}</option>
                <option value="tx">{t('gasTypes.tx')}</option>
              </select>
            </div>

            <div className="filter-column">
              <label>{t('filters.configuration')}</label>
              <select
                value={selectedConfig}
                onChange={(e) => {
                  setSelectedConfig(e.target.value);
                  if (onConfigChange) onConfigChange(e);
                }}
              >
                <option value="">{t('filters.allFeminine')}</option>
                <option value="egal">{t('filters.anyChoice')}</option>
                <option value="oc">{t('configTypes.oc')}</option>
                <option value="cc">{t('configTypes.cc')}</option>
              </select>
            </div>

            <button
              className="reset-button"
              onClick={handleResetFilters}
              disabled={!hasActiveFilters}
            >
              {t('filters.reset')}
            </button>
          </div>
        )}
      </div>

      {/* Bouton pour ouvrir le panneau de filtres (mobile uniquement) */}
      {isMobile && (
        <div className="mobile-filters-bar">
          <button
            className="filters-toggle-btn"
            onClick={() => setPanelOpen(true)}
            type="button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="4" y1="21" x2="4" y2="14"></line>
              <line x1="4" y1="10" x2="4" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12" y2="3"></line>
              <line x1="20" y1="21" x2="20" y2="16"></line>
              <line x1="20" y1="12" x2="20" y2="3"></line>
            </svg>
            {t('filters.title')}
          </button>

          <button
            className="mobile-reset-btn"
            onClick={handleResetFilters}
            disabled={!hasActiveFilters}
          >
            {t('filters.reset')}
          </button>

        </div>
      )}

      <FiltersPanel
        isOpen={isPanelOpen}
        onClose={() => setPanelOpen(false)}
        onApply={() => setPanelOpen(false)}
      >
        {/* === Filtres du panneau mobile === */}
        <div
          className={`filter-column ${showCalendar ? "show-calendar" : ""}`}
          ref={calendarRef}
        >
          <label>{t('filters.date')}</label>
          <span
            className="calendar-icon"
            title={t('filters.chooseDate')}
            onClick={() => setShowCalendar(!showCalendar)}
          >
            <CalendarDays
              size={22}
              strokeWidth={2.2}
              color="#222"
              style={{
                marginRight: 4,
                cursor: "pointer",
                verticalAlign: "middle",
              }}
            />
          </span>

          {showCalendar && (
            <div className="calendar-popup">
              <Calendar
                onChange={(date) => {
                  const d = new Date(
                    date.getFullYear(),
                    date.getMonth(),
                    date.getDate()
                  );
                  const yyyy = d.getFullYear();
                  const mm = String(d.getMonth() + 1).padStart(2, "0");
                  const dd = String(d.getDate()).padStart(2, "0");
                  const iso = `${yyyy}-${mm}-${dd}`;
                  onFilterChange(iso);
                  setShowCalendar(false);
                }}
                value={
                  filterDate
                    ? new Date(`${filterDate}T00:00:00`)
                    : new Date()
                }
                locale={i18n.language}
              />
            </div>
          )}
        </div>

        {/* Canton */}
        <div className="filter-column">
          <label>{t('filters.canton')}</label>
          <select
            value={selectedCanton}
            onChange={(e) => {
              setSelectedCanton(e.target.value);
              if (onCantonChange) onCantonChange(e);
            }}
          >
            <option value="">{t('filters.all')}</option>
            <option value="AG">{t('cantons.AG')}</option>
            <option value="AI">{t('cantons.AI')}</option>
            <option value="AR">{t('cantons.AR')}</option>
            <option value="BE">{t('cantons.BE')}</option>
            <option value="BL">{t('cantons.BL')}</option>
            <option value="BS">{t('cantons.BS')}</option>
            <option value="FR">{t('cantons.FR')}</option>
            <option value="GE">{t('cantons.GE')}</option>
            <option value="GL">{t('cantons.GL')}</option>
            <option value="GR">{t('cantons.GR')}</option>
            <option value="JU">{t('cantons.JU')}</option>
            <option value="LU">{t('cantons.LU')}</option>
            <option value="NE">{t('cantons.NE')}</option>
            <option value="NW">{t('cantons.NW')}</option>
            <option value="OW">{t('cantons.OW')}</option>
            <option value="SG">{t('cantons.SG')}</option>
            <option value="SH">{t('cantons.SH')}</option>
            <option value="SO">{t('cantons.SO')}</option>
            <option value="SZ">{t('cantons.SZ')}</option>
            <option value="TG">{t('cantons.TG')}</option>
            <option value="TI">{t('cantons.TI')}</option>
            <option value="UR">{t('cantons.UR')}</option>
            <option value="VD">{t('cantons.VD')}</option>
            <option value="VS">{t('cantons.VS')}</option>
            <option value="ZG">{t('cantons.ZG')}</option>
            <option value="ZH">{t('cantons.ZH')}</option>
          </select>
        </div>

        {/* Type */}
        <div className="filter-column">
          <label>{t('filters.type')}</label>
          <select
            value={selectedType}
            onChange={(e) => {
              setSelectedType(e.target.value);
              if (onTypeChange) onTypeChange(e);
            }}
          >
            <option value="">{t('filters.all')}</option>
            <option value="egal">{t('filters.anyChoice')}</option>
            <option value="loisir">{t('diveTypes.loisir')}</option>
            <option value="deco">{t('diveTypes.deco')}</option>
          </select>
        </div>

        {/* Gaz */}
        <div className="filter-column">
          <label>{t('filters.gas')}</label>
          <select
            value={selectedGas}
            onChange={(e) => {
              setSelectedGas(e.target.value);
              if (onGasChange) onGasChange(e);
            }}
          >
            <option value="">{t('filters.all')}</option>
            <option value="egal">{t('filters.anyChoice')}</option>
            <option value="air">{t('gasTypes.air')}</option>
            <option value="nx">{t('gasTypes.nx')}</option>
            <option value="tx">{t('gasTypes.tx')}</option>
          </select>
        </div>

        {/* Configuration */}
        <div className="filter-column">
          <label>{t('filters.configuration')}</label>
          <select
            value={selectedConfig}
            onChange={(e) => {
              setSelectedConfig(e.target.value);
              if (onConfigChange) onConfigChange(e);
            }}
          >
            <option value="">{t('filters.allFeminine')}</option>
            <option value="egal">{t('filters.anyChoice')}</option>
            <option value="oc">{t('configTypes.oc')}</option>
            <option value="cc">{t('configTypes.cc')}</option>
          </select>
        </div>

        {/* Réinitialiser */}
        <button className="reset-button" onClick={handleResetFilters}>
          {t('filters.reset')}
        </button>
      </FiltersPanel>

    </header>
  );
};

export default Header;
